import React from "react";
import { useFela } from "react-fela";

import { Grid } from "@/components/Grid";
import { useTheme } from "@/hooks/useTheme";
import { Badge } from "@/components/Badge";
import { Icon } from "@/components/Icon";
import { Heading } from "@/components/Heading";
import Oval from "@/icons/oval-copy-10.svg";

import { BadgeWithTitle } from "./BadgeWithTitle";
import { ConversionImage } from "./ConversionImage";
import { IConversionSection } from "./types";

type IProps = IConversionSection & JSX.IntrinsicElements["div"];

export const ConversionSection: React.FC<IProps> = ({
  title,
  elements,
  iconBadges,
  ...rest
}) => {
  const { css } = useFela();
  const { colors, bp } = useTheme();

  return (
    <Grid
      customStyle={{ flexDirection: "column", position: "relative" }}
      background="GHOST_WHITE"
      {...rest}
      id="ConversionSection"
    >
      <div
        className={css({
          top: -168,
          position: "absolute",
          transform: "rotate(90deg)",
          display: "none",
          [bp.FROM_DESKTOP]: { display: "block", left: "-25vw" },
          [bp.FROM_LARGE_DESKTOP]: {
            left: "-20vw",
          },
          nested: {
            "@media (min-width: 1800px)": { left: "-25vw" },
          },
        })}
      >
        <Oval />
      </div>
      <div className={css({ textAlign: "center" })}>
        <Heading as="h2">{title}</Heading>

        {iconBadges && (
          <div
            className={css({
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "space-evenly",
              [bp.FROM_TABLET]: {
                flexWrap: "no-wrap",
              },
            })}
          >
            {iconBadges.map((badge, index) => (
              <BadgeWithTitle key={index} {...badge} />
            ))}
          </div>
        )}
      </div>

      {elements && (
        <div
          className={css({
            display: "flex",
            margin: "50px 0",
            alignItems: "center",
            flexDirection: "column",
            [bp.FROM_TABLET]: {
              margin: "70px 0",
            },
            [bp.FROM_DESKTOP]: {
              flexDirection: "row",
              margin: "90px 0",
              justifyContent: "space-between",
            },
          })}
        >
          <ConversionImage {...elements.from} />

          <Badge
            size={64}
            customStyle={{
              zIndex: 1,
              position: "relative",
              backgroundColor: colors.WHITE,
              margin: "24px 24px 60px 24px",
              boxShadow: `4px 8px 8px 0 ${colors.CETACEAN_BLUE}`,
              flexShrink: 0,
              [bp.FROM_TABLET]: {
                margin: "30px 24px 60px 24px",
              },
              [bp.FROM_DESKTOP]: {
                margin: 24,
              },
              nested: {
                "> svg": {
                  [bp.TO_DESKTOP]: {
                    transform: "rotate(90deg)",
                  },
                },
              },
            }}
          >
            <Icon iconType="ARROW-RIGHT" />
          </Badge>

          <ConversionImage {...elements.to} />
        </div>
      )}
    </Grid>
  );
};
